import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui';
import { useAuth } from '@/shared/context/AuthContext';
import { usePermission } from '@/shared/hooks/usePermission';
import { typography } from '@/shared/theme/tokens';
import {
  SHORTCUT_GROUP_LABEL,
  SHORTCUT_GROUP_ORDER,
  visibleShortcuts,
  type ShortcutDef,
  type ShortcutGroupId,
  type ShortcutMnemonicKey,
} from './catalog';
import { formatChordParts, isMacPlatform } from './formatChord';
import { Kbd } from './Kbd';

export type KeyboardShortcutsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const mnemonicMarkSx = {
  fontWeight: typography.weight.semibold,
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  color: 'text.primary',
} as const;

/** Renders mnemonic copy with `<mark>` letters emphasized to match the chord keys. */
const ShortcutMnemonic: React.FC<{ mnemonicKey: ShortcutMnemonicKey }> = ({
  mnemonicKey,
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    component="div"
    sx={{ mt: 0.25, lineHeight: 1.35 }}
  >
    <Trans
      i18nKey={mnemonicKey}
      ns="common"
      components={{
        mark: <Box component="span" sx={mnemonicMarkSx} />,
      }}
    />
  </Typography>
);

const ChordKeys: React.FC<{
  shortcut: ShortcutDef;
  mac: boolean;
  sequenceThen: string;
}> = ({ shortcut, mac, sequenceThen }) => {
  const parts = formatChordParts(shortcut.chord, mac);
  const isSequence = shortcut.chord.kind === 'sequence';

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" component="span">
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {index > 0 ? (
            <Typography component="span" variant="caption" color="text.secondary">
              {isSequence ? sequenceThen : '+'}
            </Typography>
          ) : null}
          <Kbd>{part}</Kbd>
        </React.Fragment>
      ))}
    </Stack>
  );
};

/**
 * Cheatsheet dialog listing v1 shortcuts filtered by the current user's permissions.
 */
export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const mac = isMacPlatform();
  const isAdmin = user?.role === 'admin';

  const grouped = useMemo(() => {
    const list = visibleShortcuts(hasPermission, isAdmin);
    const map = new Map<ShortcutGroupId, ShortcutDef[]>();
    for (const group of SHORTCUT_GROUP_ORDER) {
      map.set(group, []);
    }
    for (const shortcut of list) {
      map.get(shortcut.group)?.push(shortcut);
    }
    return SHORTCUT_GROUP_ORDER.map((group) => ({
      group,
      items: map.get(group) ?? [],
    })).filter((section) => section.items.length > 0);
  }, [hasPermission, isAdmin]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" aria-labelledby="keyboard-shortcuts-title">
      <DialogTitle id="keyboard-shortcuts-title">{t('keyboardShortcuts')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {grouped.map((section) => (
            <Box key={section.group}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, letterSpacing: 0.6 }}
              >
                {t(SHORTCUT_GROUP_LABEL[section.group])}
              </Typography>
              <Stack spacing={1.25}>
                {section.items.map((shortcut) => (
                  <Stack
                    key={shortcut.id}
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{t(shortcut.labelKey)}</Typography>
                      {shortcut.mnemonicKey ? (
                        <ShortcutMnemonic mnemonicKey={shortcut.mnemonicKey} />
                      ) : null}
                    </Box>
                    <Box sx={{ flexShrink: 0, pt: 0.15 }}>
                      <ChordKeys
                        shortcut={shortcut}
                        mac={mac}
                        sequenceThen={t('shortcutSequenceThen')}
                      />
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
